import { useState } from 'react';
import { SectionHeader } from '../common/SharedComponents';
import { Criteria } from './common/components/criteria/Criteria';
import { LoanSummaryModal } from './modals/loanSummaryModal/LoanSummaryModal';
import store, { lendingLimitActions } from '@apply/adminStore';
import { LendingLimitApi } from '@apply/api';
import { applicationInsights } from '../ApplicationInsights';
import { ILendingLimitSearch } from '@apply/api/src/api/LendingLimitApi';
import { WarningBanner } from './common/components/WarningBanner';
import { LendingLimitsAccordionPanel } from './common/components/lendingLimitAccordionPanel/LendingLimitAccordionPanel';
import { DirectionBanner } from './common/components/DirectionBanner';
import { ILoanHistorySummaryTableRow } from './common/components/lendingLimitAccordionPanel/components/common/constants/tables/interfaces/ILoanHistorySummaryTableRow';
import { IBLLSummary } from './common/components/lendingLimitAccordionPanel/components/common/constants/tables/interfaces/IBLLSummary';
import { buildLendingLimitTables } from './common/transforms/buildLendingLimitTables';

export const BorrowerLendingLimits = () => {
    const lendingLimitApi = new LendingLimitApi.LendingLimitApi(applicationInsights);
    const { dispatch } = store;
    const [searchPerformed, set_searchPerformed] = useState(false);
    const [, set_lendingLimitSearchResult] = useState<ILendingLimitSearch | undefined>(undefined);
    const [, set_principalId] = useState<number | undefined>(undefined);
    const [guarantor, set_guarantor] = useState<string | undefined>(undefined);
    const [business, set_business] = useState<string | undefined>(undefined);
    const [hasMultipleBusinesses, set_hasMultipleBusiness] = useState<boolean | undefined>(undefined);
    const [bllSummaryData, set_bllSummaryData] = useState<IBLLSummary | undefined>(undefined);

    const clearLendingLimitState = () => {
        set_searchPerformed(false);
        set_lendingLimitSearchResult(undefined);
        set_principalId(undefined);
        set_guarantor(undefined);
        set_business(undefined);
        set_hasMultipleBusiness(false);
        set_bllSummaryData(undefined);
    };

    const applyLendingLimitResult = (id: number, result: ILendingLimitSearch, summaryData: IBLLSummary) => {
        set_searchPerformed(true);
        set_lendingLimitSearchResult(result);
        set_principalId(id);
        set_guarantor(result.borrowerName);
        set_business(result.businessName);
        set_hasMultipleBusiness(result.hasMultipleTopLevelBusinesses);
        set_bllSummaryData(summaryData);
    };

    const onGurantorLendingLimitsSearch = async (id: number | undefined) => {
        if (!id) {
            clearLendingLimitState();
            return;
        }

        const data = await lendingLimitApi.borrowerLendingLimitSearch(id);

        if (!data || data.statusCode !== 200 || !data.result) {
            clearLendingLimitState();
            return;
        }

        const result = data.result as ILendingLimitSearch;
        const calculatedData = buildLendingLimitTables(result);
        applyLendingLimitResult(id, result, calculatedData);
    };

    const onLoanSummaryClick = (applicableLoans: ILoanHistorySummaryTableRow[] | undefined) => {
        dispatch(
            lendingLimitActions.setLendingLoanLimitSummaryModal({
                lendingLimitLoanSummaryModal: { isOpen: true, loans: applicableLoans },
            })
        );
    };

    return (
        <>
            <SectionHeader title='Borrower Lending Limits' />
            <div
                className='main-content no-search'
                style={{ padding: 0, backgroundColor: '#f4f4f4' }}
            >
                <div
                    className='scrollable-content-without-search'
                    style={{ padding: '1rem', overflowX: 'auto' }}
                >
                    <div className='lending-limits'>
                        <Criteria onGuarantorLendingLimitsSearch={onGurantorLendingLimitsSearch} />
                        {!searchPerformed ? (
                            <DirectionBanner message='Select a Borrower person in the search above to view Borrower Lending Limit details.' />
                        ) : hasMultipleBusinesses ? (
                            <WarningBanner message='Unable to load lending limits because this borrower has multiple businesses.' />
                        ) : (
                            <LendingLimitsAccordionPanel
                                guarantor={guarantor}
                                business={business}
                                hasMultipleBusinesses={hasMultipleBusinesses}
                                onLoanSummaryClick={onLoanSummaryClick}
                                bllData={bllSummaryData}
                            />
                        )}
                    </div>
                </div>
            </div>
            <LoanSummaryModal />
        </>
    );
};
